import { useCallback } from "react";
import { toast } from "sonner";
import { sellerService } from "@/features/sellers/services/seller.service";
import type { SellerResponseDTO } from "@/features/sellers/types/seller.types";
import { getApiErrorMessage } from "@/shared/lib/api-error";
import { AsyncMultiSelectChips } from "./AsyncMultiSelectChips";

interface SellerMultiSelectFieldProps {
  value: number[];
  onChange: (ids: number[]) => void;
  disabled?: boolean;
  inputId?: string;
}

const SEARCH_PAGE_SIZE = 8;

const getSellerId = (seller: SellerResponseDTO) => seller.id;
const renderSeller = (seller: SellerResponseDTO) => seller.name;

export function SellerMultiSelectField({
  value,
  onChange,
  disabled,
  inputId,
}: SellerMultiSelectFieldProps) {
  const searchFn = useCallback(async (term: string, signal: AbortSignal) => {
    const response = await sellerService.search(
      {
        name: term,
        size: SEARCH_PAGE_SIZE,
        sortBy: "name",
        sortDirection: "ASC",
      },
      { signal },
    );
    return response.content;
  }, []);

  const loadByIds = useCallback(async (ids: number[], signal: AbortSignal) => {
    const results = await Promise.all(
      ids.map((id) => sellerService.getById(id, { signal })),
    );
    return results;
  }, []);

  return (
    <AsyncMultiSelectChips<SellerResponseDTO>
      value={value}
      onChange={onChange}
      searchFn={searchFn}
      loadByIds={loadByIds}
      getOptionId={getSellerId}
      renderOption={renderSeller}
      placeholder="Buscar vendedor pelo nome…"
      emptyText="Nenhum vendedor encontrado"
      disabled={disabled}
      inputId={inputId}
      onSearchError={(e) =>
        toast.error(getApiErrorMessage(e, "Erro ao buscar vendedores"))
      }
      onLoadByIdsError={(e) =>
        toast.error(
          getApiErrorMessage(e, "Erro ao carregar vendedores selecionados"),
        )
      }
    />
  );
}
